import { Box, Container, Typography, Grid, Paper, Button, Switch, Chip, Divider, Stack, Loader,
  ExplorerPlusIcon
 } from 'src/components';
import { useState } from 'react';
import { desktopMembershipsComparison } from 'src/data';
import { createStripeCheckoutSession, useAuth } from 'src/utils';
import { useSnackbar } from 'src/hooks';
import { DesktopProfileModal } from 'src/pages/profile'

const PricingSection = () => {
  const [isYearly, setIsYearly] = useState(true);
  const [loadingStripeCheckout, setLoadingStripeCheckout] = useState(false);
  const [displayLogin, setDisplayLogin] = useState(false);

  const { user } = useAuth();
  const { openSnackbar } = useSnackbar();

  const handleCheckout = async (planName) => {
    if (planName === "Free") {
      return;
    }
    
    if (!user) {
      setDisplayLogin(true);
      return;
    }

    setLoadingStripeCheckout(true);
    try {
      const billingCycle = isYearly ? 'Yearly' : 'Monthly';
      const { url } = await createStripeCheckoutSession(
          `${planName}${billingCycle}`,
          user.id,
          user.email,
          user.stripeCustomerId
      );
      window.open(url, "_blank");
    } catch (err) {
      console.error('Failed to start checkout', err);
      openSnackbar('Please try again later', 'error');
    } finally {
        setLoadingStripeCheckout(false); 
    }
  };

  return (
    <Box sx={{ py: 6, bgcolor: '#fafafa' }}>
      <Container>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 700, 
              mb: 2
            }}
          >
            Choose Your Plan
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: '#666',
              fontWeight: 400,
              maxWidth: 600,
              mx: 'auto',
              mb: 3
            }}
          >
            Start your overlanding adventure with the perfect plan for your needs
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <Typography sx={{ color: isYearly ? '#999' : '#1a1a1a', fontWeight: 600 }}>
              Monthly
            </Typography>
            <Switch
              checked={isYearly}
              onChange={(e) => setIsYearly(e.target.checked)}
            />
            <Typography sx={{ color: isYearly ? '#1a1a1a' : '#999', fontWeight: 600 }}>
              Yearly
            </Typography>
            {isYearly && (
              <Chip 
                label="Save 35%" 
                size="small" 
                sx={{ 
                  bgcolor: '#e3f2fd', 
                  fontWeight: 600
                }} 
              />
            )}
          </Box>
        </Box>

        <Grid container spacing={4} sx={{ justifyContent: 'center', maxWidth: 1200, mx: 'auto' }}>
          {desktopMembershipsComparison.map((plan, index) => (
            <Grid key={index} size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: plan.popular ? '2px solid' : '1px solid #e5e5e5',
                  bgcolor: plan.popular ? '#f8fbff' : 'white',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Chip 
                    label={plan.chip}
                    sx={{
                      mb: 2,
                      bgcolor: plan.popular ? 'primary.main' : '#f5f5f5',
                      color: plan.popular ? 'white' : '#666',
                      fontWeight: 600
                    }}
                  />

                  <Box sx={{position: 'absolute', top: 25, left: 20}}>
                      {plan.name !== "Free" && (
                        <ExplorerPlusIcon 
                          tier={plan.name.toLowerCase()}
                          variant={plan.name === "Plus" ? "static" : "animated"}
                        />
                      )}
                    
                  </Box>

                  <Typography variant="h4" sx={{ fontWeight: 700, ml: 1 }}>
                      {plan.name}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="h2" 
                      component="span"
                      sx={{ 
                        fontWeight: 700,
                        color: '#1a1a1a'
                      }}
                    >
                      £{plan.monthlyPrice === 0 ? 0 : isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      component="span"
                      sx={{ color: '#666', ml: 1 }}
                    >
                      {plan.monthlyPrice === 0 ? '' : '/month'}
                    </Typography>
                    {isYearly && plan.monthlyPrice > 0 && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#888', 
                          textDecoration: 'line-through',
                          display: 'block',
                          mt: 0.5
                        }}
                      >
                        £{plan.monthlyPrice}/month
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ flex: 1, mb: 3 }}>
                  <Stack spacing={2}>
                    {plan.features.map((feature, featureIndex) => (
                      <Box 
                        key={featureIndex}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: 2
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: '#e8f5e8',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.8rem',
                            color: '#2e7d32',
                            fontWeight: 600
                          }}
                        >
                          {feature.icon}
                        </Box>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {feature.text}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Button
                  variant={plan.popular ? 'contained' : 'outlined'}
                  size="large"
                  fullWidth
                  disabled={loadingStripeCheckout}
                  onClick={() => handleCheckout(plan.name)}
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    ...(plan.popular && {
                      bgcolor: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      }
                    })
                  }}
                >
                  {plan.buttonText}
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {loadingStripeCheckout && <Loader />}

      {displayLogin && <DesktopProfileModal displayLogin={displayLogin} setDisplayLogin={setDisplayLogin}/>}

    </Box>
  );
};

export default PricingSection;